import * as THREE from '@phdsoft/three';
import { InspectionPointInstanceManager } from './inspection-point-instance-manager.service';

/**
 * Minimal owner interface used by PhantomSurfaceDesign to avoid a circular
 * import with GaugingElement. GaugingElement satisfies this interface via
 * its inherited Guid property.
 *
 * Using a minimal interface (rather than importing GaugingElement directly)
 * prevents the circular dependency:
 *   gaugingElement → phantom-surface-design → inspection-point-instance-manager → gaugingElement
 */
export interface PhantomOwner {
  Guid: string;
}

/**
 * Lightweight proxy that replaces a real THREE.Mesh as GaugingElement.SurfaceDesign
 * for point inspection elements.
 *
 * Design rationale (VIS-03):
 *   The legacy code has 100+ access sites that read/write GaugingElement.SurfaceDesign
 *   properties. Replacing the real mesh with a phantom proxy object that duck-types the
 *   accessed property surface eliminates the need to change any call sites.
 *
 *   The phantom is intentionally NOT added to any THREE.js scene group. The InstancedMesh
 *   (which actually renders the point) lives in section.surfaceGroup instead.
 *
 *   Memory footprint: ~100 bytes per instance vs ~500-800 bytes for a real THREE.Mesh.
 *   At 10,000 inspection points this saves ~7 MB of memory.
 *
 * Property surface (from full codebase audit — see 02-RESEARCH.md):
 *   .visible (r/w)    — setter forwards to instanceManager.setInstanceVisible
 *   .material (r/w)   — setter forwards to instanceManager.setInstanceColor (REND-04 intercept)
 *   .name (r)         — returns owner.Guid
 *   .position (r)     — stored from constructor parameter
 *   .userData (r/w)   — plain object (populated by caller with SectionID etc.)
 *   .children (r)     — always empty array (clearBorder reads children.length)
 *   .parent (r)       — always null (GaugingElement overrides isVisible/realVisibility
 *                       in Plan 03 to avoid null-dereference in parent traversal)
 *   .add()            — no-op (called by createBorder)
 *   .dispose()        — no-op (guard against section teardown traversals)
 *
 * IMPORTANT: This class does NOT extend THREE.Object3D. At the call site where
 * GaugingElement.SurfaceDesign is assigned, cast via `as unknown as THREE.Mesh`.
 * Plan 02-02 performs this cast in loadInspectionElements.
 */
export class PhantomSurfaceDesign {
  /** Matches THREE.Mesh.name — selection service uses object.name === Guid lookup. */
  public name: string;

  /** Stored position — read/written by drag service and syncInstance. */
  public position: THREE.Vector3;

  /** Stored rotation (Euler) — synced with quaternion like THREE.Object3D.
   *  Drag service reads/writes rotation; syncInstance reads quaternion.
   *  The _onChange callback keeps them in sync. */
  public rotation: THREE.Euler;

  /** Stored quaternion — read/written by drag service and syncInstance. */
  public quaternion: THREE.Quaternion;

  /** Stored scale — read/written by drag service and syncInstance. */
  public scale: THREE.Vector3;

  /**
   * Plain userData bag — populated by caller with SectionID, scale, etc.
   * Matches the userData pattern on real THREE.Object3D instances.
   */
  public userData: Record<string, any> = {};

  /**
   * Always empty array — clearBorder iterates this to dispose border children.
   * Returning an empty array is safe: no dispose calls will be made.
   */
  public children: any[] = [];

  /**
   * Always null — phantom is not in scene graph.
   * GaugingElement.isVisible / realVisibility is overridden in Plan 03 to avoid
   * the null-dereference that occurs when the parent chain is traversed on a
   * non-Object3D phantom (GeometricElement.realVisibility line 71-75).
   */
  public parent: THREE.Object3D | null = null;

  /** Private backing field for the visible state. */
  private _visible: boolean = true;

  /** Private backing field for the material. */
  private _material: THREE.Material | null = null;

  /**
   * @param _owner      The GaugingElement that owns this phantom (via PhantomOwner interface).
   * @param _manager    The InstancedMesh manager — receives visibility/color forwarding calls.
   * @param position    The world position of the inspection point (from original pointMesh).
   * @param quaternion  The rotation (optional — defaults to identity quaternion).
   * @param scale       The scale (optional — defaults to unit scale).
   */
  constructor(
    private readonly _owner: PhantomOwner,
    private readonly _manager: InspectionPointInstanceManager,
    position: THREE.Vector3,
    quaternion?: THREE.Quaternion,
    scale?: THREE.Vector3,
  ) {
    this.name = _owner.Guid;
    this.position = position.clone();
    this.quaternion = quaternion ? quaternion.clone() : new THREE.Quaternion();
    this.scale = scale ? scale.clone() : new THREE.Vector3(1, 1, 1);

    // Mirror THREE.Object3D: rotation (Euler) ↔ quaternion stay in sync.
    // Drag service writes rotation; syncInstance reads quaternion.
    this.rotation = new THREE.Euler();
    this.rotation.setFromQuaternion(this.quaternion);
    this.rotation._onChange(() => {
      this.quaternion.setFromEuler(this.rotation, false);
    });
  }

  // ---------------------------------------------------------------------------
  // Intercepted property accessors
  // ---------------------------------------------------------------------------

  /**
   * Getter returns the local _visible mirror.
   * The authoritative visibility state is in the manager (_visibilityState map),
   * but the phantom tracks a local copy for the debounce check in the setter.
   */
  get visible(): boolean {
    return this._visible;
  }

  /**
   * Setter intercept (VIS-05): forwards the visibility change to the manager.
   *
   * Debounce: skips the call if value equals current state to avoid redundant
   * GPU writes (zero-scale matrix + dirty flag).
   *
   * All 30+ write sites in asset-manager-filter.ts
   * (`gaugingElement.SurfaceDesign.visible = bool`) flow through here
   * without any changes at those call sites.
   */
  set visible(v: boolean) {
    if (this._visible === v) return;
    this._visible = v;
    this._manager.setInstanceVisible(this._owner as any, v);
  }

  /**
   * Getter returns the stored material.
   * GeometricElement.currentMaterial reads `(element as SurfaceElement).currentMaterial`
   * which in turn reads SurfaceDesign.material — this getter satisfies that read.
   */
  get material(): THREE.Material | null {
    return this._material;
  }

  /**
   * Setter intercept (REND-04 — Pitfall 4 from RESEARCH.md):
   *
   * GeometricElement.defaultMaterial setter writes `this.SurfaceDesign.material = v`
   * which is the path through which updateInspectionColor applies corrosion colors.
   *
   * The phantom traps this write and:
   *   1. Stores the material locally (for reads via getter above).
   *   2. Forwards to instanceManager.setInstanceColor to update the per-instance
   *      GPU color via setColorAt (actual needsUpdate deferred to flushDirtyBuffers).
   *
   * If v is null (teardown), stores null and skips the color update.
   */
  set material(v: THREE.Material | null) {
    this._material = v;
    if (v !== null) {
      this._manager.setInstanceColor(this._owner as any, v);
    }
  }

  // ---------------------------------------------------------------------------
  // Method stubs
  // ---------------------------------------------------------------------------

  /**
   * No-op. Called by GeometricElement.createBorder() when adding LineSegments
   * children to SurfaceDesign. For phantom objects, borders are not rendered.
   */
  add(_child: any): void {
    // Intentional no-op — phantom is not in scene graph
  }

  /**
   * No-op. Called during section teardown traversal that calls dispose()
   * on scene graph children. The phantom guards against this traversal.
   * The actual InstancedMesh disposal is handled by InspectionPointInstanceManager.destroy().
   */
  dispose(): void {
    // Intentional no-op — lifecycle managed by InspectionPointInstanceManager
  }

  /**
   * Minimal traverse stub. Called by OutlinePass and other THREE.js post-processing
   * effects when the phantom ends up in selectedObjects. Calls the callback with
   * `this` (no children to recurse). The OutlinePass will find no geometry and
   * skip rendering — correct behavior since the InstancedMesh renders the point.
   */
  traverse(callback: (object: any) => void): void {
    callback(this);
  }

  /**
   * No-op. Called by Box3.expandByObject when Section.addSurfaceElement
   * computes bounding boxes. The phantom has no world matrix to update.
   *
   * Without this stub, registering inspection point elements in the
   * InspectionCampaign's internal region/section hierarchy would crash
   * because expandByObject calls object.updateWorldMatrix(false, false).
   */
  updateWorldMatrix(_updateParents?: boolean, _updateChildren?: boolean): void {
    // Intentional no-op — phantom has no world transform
  }
}
